import type { Generation, Me, PaymentMethod, TrendSection } from '@trendator/shared';
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api, type CreateGenerationRequest } from './api';
import { LowBalanceSheet } from './components/LowBalanceSheet';
import { TabBar, type Tab } from './components/TabBar';
import { clearPending, savePending, takePending } from './pending';
import { Create, type InsufficientFunds } from './screens/Create';
import { Catalog } from './screens/Catalog';
import { GenerationStatus } from './screens/GenerationStatus';
import { History } from './screens/History';
import { Main } from './screens/Main';
import { Onboarding } from './screens/Onboarding';
import { PaymentStatus } from './screens/PaymentStatus';
import { Loading, Message } from './screens/Status';
import { TopUp, type TopUpPreset } from './screens/TopUp';
import { TrendScreen } from './screens/TrendScreen';
import { Twin } from './screens/Twin';
import { TwinSetup } from './screens/TwinSetup';
import { Profile } from './screens/Profile';
import { Withdraw } from './screens/Withdraw';
import { DuoScreen } from './screens/Duo';
import { getInitData, getStartParam, openPayUrl } from './telegram';
import { startTracking, track } from './analytics';

type State =
  | { kind: 'loading' }
  | { kind: 'outside' }
  | { kind: 'error' }
  | { kind: 'ready'; me: Me };

/** Экраны поверх вкладок. */
type Route =
  | { name: 'tabs' }
  | { name: 'create'; initial?: CreateInitial }
  | { name: 'generation'; id: string }
  | { name: 'topup'; preset?: TopUpPreset }
  | { name: 'payment'; id: string }
  | { name: 'catalog'; section?: TrendSection }
  | { name: 'trend'; idOrSlug: string; rank?: number; from: 'tabs' | 'catalog' }
  | { name: 'twinSetup' }
  | { name: 'withdraw' }
  | { name: 'duo'; code: string };

/** Чем заполнить экран «Создать» при «Повторить». */
export type CreateInitial = Partial<Exclude<CreateGenerationRequest, { kind: 'trend' }>>;

const PAY_PARAM = /^pay_([0-9a-f-]{36})$/i;
const TREND_PARAM = /^t_([a-z0-9][a-z0-9-]{1,47})$/;
const DUO_PARAM = /^d_([a-z0-9]{10})$/;

/**
 * Вход по ссылке: `?startapp=pay_<id>` — вернулись с платёжки, `?startapp=t_<slug>` — ссылка на тренд.
 */
function initialRoute(): Route {
  const param = getStartParam() ?? '';
  const pay = PAY_PARAM.exec(param);
  if (pay) return { name: 'payment', id: pay[1]! };
  const duo = DUO_PARAM.exec(param);
  if (duo) return { name: 'duo', code: duo[1]! };
  const trend = TREND_PARAM.exec(param);
  if (trend) return { name: 'trend', idOrSlug: trend[1]!, from: 'tabs' };
  return { name: 'tabs' };
}

export function App() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [tab, setTab] = useState<Exclude<Tab, 'create'>>('main');
  const [route, setRoute] = useState<Route>(initialRoute);
  const [lowBalance, setLowBalance] = useState<(InsufficientFunds & { methods: PaymentMethod[] }) | null>(null);

  const login = useCallback(async () => {
    if (!getInitData()) {
      setState({ kind: 'outside' });
      return;
    }
    setState({ kind: 'loading' });
    try {
      setState({ kind: 'ready', me: await api.auth() });
      startTracking();
    } catch (err) {
      setState(err instanceof ApiError && err.status === 401 ? { kind: 'outside' } : { kind: 'error' });
    }
  }, []);

  useEffect(() => {
    void login();
  }, [login]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [tab, route]);

  // Экран = маршрут поверх вкладок или вкладка. Без id генераций и платежей.
  const screen = route.name === 'tabs' ? tab : route.name;
  useEffect(() => {
    if (state.kind === 'ready') track('screen_view', { screen });
  }, [screen, state.kind]);

  const setBalance = useCallback((balance: number) => {
    setState((s) => (s.kind === 'ready' && s.me.balance !== balance ? { ...s, me: { ...s.me, balance } } : s));
  }, []);

  /** Генерация создана — бесплатная попытка потрачена (если была). */
  const onGenerationCreated = useCallback((id: string) => {
    setState((s) => (s.kind === 'ready' ? { ...s, me: { ...s.me, freeGenerationAvailable: false } } : s));
    setRoute({ name: 'generation', id });
  }, []);

  const openLowBalance = useCallback(async (data: InsufficientFunds) => {
    setBalance(data.balance);
    let methods: PaymentMethod[] = [];
    try {
      methods = (await api.paymentsConfig()).methods;
    } catch {
      /* без способов кнопка будет неактивна */
    }
    setLowBalance({ ...data, methods });
    track('paywall_view', { kind: data.request.kind, price: data.price });
  }, [setBalance]);

  /** «Оплатить и создать»: запоминаем запрос, после оплаты запустим сами. */
  const payAndCreate = useCallback(async (packageId: string, method: PaymentMethod) => {
    if (!lowBalance) return;
    savePending(lowBalance.request);
    try {
      const payment = await api.createPayment(packageId, method);
      setLowBalance(null);
      if (payment.payUrl) {
        try {
          openPayUrl(payment.payUrl);
        } catch {
          /* откроет кнопкой на экране ожидания */
        }
      }
      setRoute({ name: 'payment', id: payment.id });
    } catch {
      clearPending();
      setLowBalance(null);
      setRoute({ name: 'topup', preset: { packageId, method } });
    }
  }, [lowBalance]);

  const onPaid = useCallback(async () => {
    const pending = takePending();
    if (!pending) return;
    try {
      const g = await api.createGeneration(pending);
      onGenerationCreated(g.id);
    } catch {
      /* не вышло — юзер останется на «Готово» и запустит сам */
    }
  }, [onGenerationCreated]);

  switch (state.kind) {
    case 'loading':
      return <Loading />;
    case 'outside':
      return <Message title="Открой в Telegram" text="Trendator работает внутри Telegram. Открой бота и нажми «Открыть»." />;
    case 'error':
      return (
        <Message
          title="Нет связи"
          text="Не получилось загрузиться. Проверь интернет."
          action={{ label: 'Ещё раз', onClick: () => void login() }}
        />
      );
    case 'ready': {
      const { me } = state;
      if (!me.hasConsent) {
        return <Onboarding onAccept={async () => setState({ kind: 'ready', me: await api.consent() })} />;
      }
      const home = () => {
        setRoute({ name: 'tabs' });
        setTab('main');
      };

      const lowBalanceSheet = lowBalance ? (
        <LowBalanceSheet
          balance={lowBalance.balance}
          price={lowBalance.price}
          methods={lowBalance.methods}
          kind={lowBalance.request.kind}
          onPay={(pkg, m) => void payAndCreate(pkg, m)}
          onClose={() => setLowBalance(null)}
        />
      ) : null;
      const repeat = (g: Generation) => {
        if (g.source === 'trend' && g.trendId) setRoute({ name: 'trend', idOrSlug: g.trendId, from: 'tabs' });
        else if (g.kind === 'video' && g.prompt) {
          setRoute({ name: 'create', initial: { kind: 'video', prompt: g.prompt, aspectRatio: (g.aspectRatio ?? '9:16') as '9:16' } });
        } else if (g.kind === 'song' && g.prompt) {
          setRoute({ name: 'create', initial: { kind: 'song', prompt: g.prompt } });
        } else if (g.mode && g.prompt) {
          setRoute({ name: 'create', initial: { kind: 'photo', mode: g.mode, prompt: g.prompt, aspectRatio: (g.aspectRatio ?? '9:16') as '9:16' } });
        } else setRoute({ name: 'create' });
      };

      if (route.name === 'catalog') {
        return (
          <Catalog
            me={me}
            initialSection={route.section}
            onBack={home}
            onOpen={(t) => setRoute({ name: 'trend', idOrSlug: t.id, from: 'catalog' })}
          />
        );
      }
      if (route.name === 'trend') {
        return (
          <>
            <TrendScreen
              key={route.idOrSlug}
              me={me}
              idOrSlug={route.idOrSlug}
              rank={route.rank}
              onBack={() => (route.from === 'catalog' ? setRoute({ name: 'catalog' }) : home())}
              onCreated={onGenerationCreated}
              onInsufficient={(d) => void openLowBalance(d)}
              onOpenTwin={() => {
                setRoute({ name: 'tabs' });
                setTab('twin');
              }}
              onDuo={(code) => setRoute({ name: 'duo', code })}
            />
            {lowBalanceSheet}
          </>
        );
      }
      if (route.name === 'create') {
        return (
          <main className="screen screen--create">
            <Create
              key={JSON.stringify(route.initial ?? {})}
              me={me}
              initial={route.initial}
              onClose={home}
              onToTrends={() => setRoute({ name: 'catalog' })}
              onCreated={onGenerationCreated}
              onInsufficient={(d) => void openLowBalance(d)}
            />
            {lowBalanceSheet}
          </main>
        );
      }
      if (route.name === 'generation') {
        return (
          <GenerationStatus
            key={route.id}
            generationId={route.id}
            onBalance={setBalance}
            onHome={home}
            onRepeat={repeat}
          />
        );
      }
      if (route.name === 'topup') {
        return (
          <TopUp
            balance={me.balance}
            preset={route.preset}
            onBack={() => setRoute({ name: 'tabs' })}
            onPaymentCreated={(id) => setRoute({ name: 'payment', id })}
          />
        );
      }
      if (route.name === 'withdraw') {
        return (
          <Withdraw
            onBack={() => {
              setRoute({ name: 'tabs' });
              setTab('profile');
            }}
            onBalance={setBalance}
          />
        );
      }
      if (route.name === 'duo') {
        return <DuoScreen key={route.code} code={route.code} onBack={home} onGeneration={(id) => setRoute({ name: 'generation', id })} onBalance={setBalance} />;
      }
      if (route.name === 'twinSetup') {
        const back = () => {
          setRoute({ name: 'tabs' });
          setTab('twin');
        };
        return <TwinSetup onBack={back} onDone={back} />;
      }
      if (route.name === 'payment') {
        return (
          <PaymentStatus
            key={route.id}
            paymentId={route.id}
            onBalance={setBalance}
            onHome={home}
            onRetry={(preset) => setRoute({ name: 'topup', preset })}
            onPaid={() => void onPaid()}
          />
        );
      }
      return (
        <main className="screen screen--tabs">
          {tab === 'main' && (
            <Main
              me={me}
              onNavigate={(t) => (t === 'create' ? setRoute({ name: 'create' }) : setTab(t))}
              onTopUp={() => setRoute({ name: 'topup' })}
              onOpenTrend={(t, rank) => setRoute({ name: 'trend', idOrSlug: t.id, rank, from: 'tabs' })}
              onCatalog={() => setRoute({ name: 'catalog' })}
            />
          )}
          {tab === 'history' && <History onOpen={(id) => setRoute({ name: 'generation', id })} />}
          {tab === 'twin' && <Twin onLiveness={() => setRoute({ name: 'twinSetup' })} />}
          {tab === 'profile' && (
            <Profile
              me={me}
              onMe={(m) => setState({ kind: 'ready', me: m })}
              onWithdraw={() => setRoute({ name: 'withdraw' })}
              onTwin={() => setTab('twin')}
            />
          )}
          <TabBar
            active={tab}
            onChange={(t) => {
              if (t === 'create') {
                track('create_tab');
                setRoute({ name: 'create' });
              } else setTab(t);
            }}
          />
        </main>
      );
    }
  }
}
